import { useQuery } from '@tanstack/react-query';
import type { ReactElement, ReactNode } from 'react';
import { Link, Navigate, Route, useLocation, useParams } from 'react-router-dom';
import { useI18n } from '../i18n/I18nProvider';
import {
  AcceptInvitationScreen, ConfirmPhoneChangeScreen, ForgotPasswordScreen, RecoveryLinkScreen,
} from '../ui/AuthFlows';
import { LegalPage } from '../ui/Legal';
import { Button, Notice, Spinner, Stack } from '../ui/primitives';
import { AuthPreferences, BrandPanel, ChooseMembershipScreen, SignInScreen, type BrandPanelProps } from '../ui/SignIn';
import { useApi } from './ApiProvider';
import {
  ENTRY_PATHS, EntrySlugContext, entryPath, hasSlugShape, slugOfBarePath, type EntryScreen,
} from './entryPaths';
import { RequireAnonymous } from './guards';
import { useSession } from './SessionProvider';

export interface EntryRoutesOptions {
  /** Declare the slug-prefixed forms too: the three nursery apps do, the platform console does not. */
  slugs?: boolean;
}

/**
 * The routes every app declares before its signed-in shell, from `ENTRY_PATHS`, as a fragment for `<Routes>`.
 * Called as a function, because the router reads `<Route>` elements and a component of its own would hide them.
 */
export function entryRoutes(brand: BrandPanelProps, { slugs = false }: EntryRoutesOptions = {}): ReactElement {
  return (
    <>
      {ENTRY_PATHS.filter((entry) => slugs || !entry.slug).map((entry) => (
        <Route
          key={entry.path}
          path={entry.path}
          element={entry.slug ? <SlugEntry screen={entry.screen} brand={brand} /> : screenFor(entry.screen, brand)}
        />
      ))}
    </>
  );
}

function screenFor(screen: EntryScreen, brand: BrandPanelProps, redirectTo?: string): ReactElement {
  switch (screen) {
    case 'sign-in':
      return <RequireAnonymous><SignInScreen {...brand} redirectTo={redirectTo} /></RequireAnonymous>;
    case 'choose':
      return <ChooseMembershipScreen {...brand} />;
    case 'invitation':
      return <AcceptInvitationScreen {...brand} />;
    case 'forgot-password':
      return <RequireAnonymous><ForgotPasswordScreen {...brand} /></RequireAnonymous>;
    // Open whether or not a session exists: the link may be opened on the new phone, signed out.
    case 'confirm-phone':
      return <ConfirmPhoneChangeScreen {...brand} />;
    // A one-time link from the platform team (Q25); open signed in or not, like the phone-change link.
    case 'recover':
      return <RecoveryLinkScreen {...brand} />;
    case 'privacy':
    case 'terms':
      return <LegalPage {...brand} document={screen} />;
  }
}

/**
 * An entry screen under a nursery's slug: the nursery's name and logo, read from its public branding, and the
 * slug kept in every link the screen offers. A slug that names no nursery falls back to the plain screen, with
 * whatever the visitor was on their way to.
 */
function SlugEntry({ screen, brand }: { screen: EntryScreen; brand: BrandPanelProps }) {
  const { slug = '' } = useParams();
  const api = useApi();
  const { locale, t } = useI18n();
  const location = useLocation();
  const { status, session } = useSession();
  const shaped = hasSlugShape(slug);

  const branding = useQuery({
    queryKey: ['entry', 'branding', slug],
    queryFn: () => api.branding(slug),
    enabled: shaped,
    retry: false,
    staleTime: 5 * 60_000,
  });

  if (!shaped || branding.isError) {
    return <Navigate to={entryPath(screen, null) + location.search} replace state={location.state} />;
  }
  if (branding.isPending) return <Centred><Spinner label={t('state.loading')} /></Centred>;

  const nursery = (locale === 'en' && branding.data.nameEn) || branding.data.nameAr;
  const nurseryBrand: BrandPanelProps = { name: nursery, tagline: brand.name, logoUrl: branding.data.logoUrl };

  // Sent here from a page that needed a session (state), or by a tapped push (`?from=`, Q68), which names the
  // nursery so a phone signed in elsewhere is asked first. Sent from the bare `/nuvakids`, home is where to land.
  const from = (location.state as { from?: string } | null)?.from
    ?? localPath(new URLSearchParams(location.search).get('from'));
  const redirectTo = from && from !== `/${slug}` ? from : '/';

  if (screen === 'sign-in' && status === 'authenticated') {
    return session?.membership.tenantSlug === slug
      ? <Navigate to={redirectTo} replace />
      : <OtherNursery brand={nurseryBrand} nursery={nursery} />;
  }

  return <EntrySlugContext.Provider value={slug}>{screenFor(screen, nurseryBrand, redirectTo)}</EntrySlugContext.Provider>;
}

/** A path inside this app, or null: a `from` from a query string is never followed off the site. */
export function localPath(value: string | null): string | null {
  return value && value.startsWith('/') && !value.startsWith('//') && !value.includes('\\') ? value : null;
}

/**
 * Signed in at one nursery and opening another's link: say so, and offer to sign out and continue. Push stays on:
 * the person signs in again at once, mostly to read the push they tapped, and that sign-in takes the phone's
 * subscription over (decision 2026-10-16 #9). Signing out anywhere else still turns it off.
 */
function OtherNursery({ brand, nursery }: { brand: BrandPanelProps; nursery: string }) {
  const { t } = useI18n();
  const { session, signOut } = useSession();
  const current = session?.membership.tenantNameAr ?? '';

  return (
    <div className="dg-auth">
      <div className="dg-auth__panel">
        <BrandPanel {...brand} />
        <div className="dg-card">
          <Stack>
            <Notice tone="info" title={t('auth.entry.otherNursery.title', { nursery })}>
              {t('auth.entry.otherNursery.body', { current, nursery })}
            </Notice>
            <Button block onClick={() => void signOut({ keepPush: true })}>{t('auth.entry.signOutAndContinue')}</Button>
            <Link to="/">{t('auth.entry.stay', { current })}</Link>
          </Stack>
        </div>
        <AuthPreferences />
      </div>
    </div>
  );
}

/**
 * The signed-in shell's not-found route, which also takes a bare `/nuvakids` to that nursery's entry: the
 * sign-in there sends someone already in it home, and offers anyone else to switch.
 */
export function EntryOrNotFound({ children }: { children: ReactNode }) {
  const location = useLocation();
  const slug = slugOfBarePath(location.pathname);
  return slug ? <Navigate to={entryPath('sign-in', slug)} replace /> : <>{children}</>;
}

function Centred({ children }: { children: ReactNode }) {
  return <div className="dg-route-spinner">{children}</div>;
}
